import React, { useState } from 'react';
import { Phone, Mail, Instagram, Facebook, MapPin, Send, Clock, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo, services } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (mock)
    await new Promise((resolve) => setTimeout(resolve, 1500));

    toast.success('Thank you! We\'ll contact you soon for your free estimate.');
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-20 bg-bg-subtle">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-primary/10 border border-brand-primary/30 rounded-full px-4 py-2 mb-6">
              <span className="text-brand-dark text-sm font-semibold">
                Get In Touch
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Request Your
              <span className="text-brand-dark"> Free Estimate</span>
            </h2>

            <p className="text-text-secondary text-lg leading-relaxed mb-10">
              Ready to transform your outdoor space? Contact us today for a free,
              no-obligation estimate. We're here to help bring your vision to
              life!
            </p>

            {/* Contact Cards */}
            <div className="space-y-4 mb-10">
              <a
                href={`tel:${companyInfo.phone}`}
                className="flex items-center gap-4 bg-bg-card rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-0.5"
              >
                <div className="w-14 h-14 bg-brand-primary rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="text-brand-dark" size={24} />
                </div>
                <div>
                  <p className="text-text-light text-sm mb-1">Call Us Anytime</p>
                  <p className="text-text-primary font-bold text-xl">
                    {companyInfo.phone}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-4 bg-bg-card rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-0.5"
              >
                <div className="w-14 h-14 bg-brand-dark rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-text-light text-sm mb-1">Email Us</p>
                  <p className="text-text-primary font-bold text-lg">
                    {companyInfo.email}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4 bg-bg-card rounded-2xl p-5 shadow-sm">
                <div className="w-14 h-14 bg-red-500 rounded-xl flex items-center justify-center">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-text-light text-sm mb-1">Location</p>
                  <p className="text-text-primary font-bold text-lg">
                    {companyInfo.location}
                  </p>
                </div>
              </div>

              <a
                href={companyInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-bg-card rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-0.5"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Instagram className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-text-light text-sm mb-1">Follow Us</p>
                  <p className="text-text-primary font-bold text-lg">
                    {companyInfo.instagram}
                  </p>
                </div>
              </a>

              <a
                href={companyInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-bg-card rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group hover:-translate-y-0.5"
              >
                <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Facebook className="text-white" size={24} />
                </div>
                <div>
                  <p className="text-text-light text-sm mb-1">Like Us</p>
                  <p className="text-text-primary font-bold text-lg">
                    Facebook
                  </p>
                </div>
              </a>
            </div>

            {/* Business Hours */}
            <div className="bg-brand-dark rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={24} className="text-brand-primary" />
                <h3 className="font-bold text-lg">Quick Response Time</h3>
              </div>
              <p className="text-white/80">
                We typically respond to all inquiries within 24 hours. For urgent
                matters, please call us directly!
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-bg-card rounded-[32px] p-8 md:p-10 shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle className="text-brand-dark" size={24} />
              <span className="text-brand-dark font-semibold">
                Free Estimates Available
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-text-primary font-medium mb-2">
                  Your Name *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder=""
                  required
                  className="h-12 rounded-xl border-border-medium focus:border-brand-dark focus:ring-brand-dark/20"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-text-primary font-medium mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder=""
                    required
                    className="h-12 rounded-xl border-border-medium focus:border-brand-dark focus:ring-brand-dark/20"
                  />
                </div>
                <div>
                  <label className="block text-text-primary font-medium mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder=""
                    className="h-12 rounded-xl border-border-medium focus:border-brand-dark focus:ring-brand-dark/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-primary font-medium mb-2">
                  Service Needed
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full h-12 rounded-xl border border-border-medium bg-transparent px-3 focus:border-brand-dark focus:ring-1 focus:ring-brand-dark/20 outline-none text-text-primary"
                >
                  <option value="">Select a service...</option>
                  {services.slice(0, -1).map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-text-primary font-medium mb-2">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project or needs..."
                  required
                  rows={4}
                  className="rounded-xl border-border-medium focus:border-brand-dark focus:ring-brand-dark/20 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-dark text-white hover:bg-brand-hover rounded-full py-6 text-lg font-bold flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Get My Free Estimate
                  </>
                )}
              </Button>

              <p className="text-text-light text-sm text-center">
                We respect your privacy. Your information will never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
